/* ═══════════════════════════════════════════════════════════════════════════
   ALIA · booking widget + private-events lead form
   Theme: c10-vinyl

   Shadow-DOM isolated. Every colour is var(--theme-token, <that token's own
   value>): custom properties inherit through the shadow boundary, so the
   widget reads the theme's live :root and the literal fallback is the same
   value in case the stylesheet fails. Type is the theme's own Chivo /
   Chivo Mono — @font-face is document-scoped, so it resolves in here.

   TRAP A — the [data-act] click handler is bound to `document`, not to the
   shadow root. Shadow DOM is a boundary in BOTH directions: a listener on the
   shadow root never sees the Corporate buttons out on the page, so every CTA
   in the header, hero, lanes, bands, blog and articles would silently do
   nothing. One document-level handler walking e.composedPath() covers page
   buttons and shadow buttons alike, with no double-firing.

   Config comes from window.ALIA_CFG, emitted by layout/theme.liquid out of
   theme settings, so the phone / OpenTable / email / endpoint live in the
   theme editor rather than in this file.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (document.getElementById('alia-wgt-host')) return;

  var C = window.ALIA_CFG || {};
  var CFG = {
    phoneHref:  C.phoneHref  || 'tel:+15145662542',
    phoneLabel: C.phoneDisplay || '(514) 566-2542',
    openTable:  C.openTable  || 'https://www.opentable.ca/r/alia-montreal',
    endpoint:   C.endpoint   || '',
    site:       C.site       || C.domain || location.hostname
  };
  var DEMO = !CFG.endpoint;

  var CSS = `
  :host{
    --w-ground:   var(--roast-0,#150D07);
    --w-roast:    var(--roast,#241A11);
    --w-panel:    var(--roast-2,#2C2015);
    --w-panel-2:  var(--roast-3,#37281A);
    --w-deep:     var(--deep,#4A331F);
    --w-ink:      var(--bone,#EEEDE3);
    --w-mute:     var(--mute,rgba(238,237,227,.60));
    --w-mute-2:   var(--mute-2,rgba(238,237,227,.38));
    --w-line:     var(--line-2,rgba(238,237,227,.09));
    --w-line-am:  var(--line,rgba(138,95,58,.30));
    --w-amber:    var(--amber,#8A5F3A);
    --w-amber-lit:var(--amber-lit,#C6884E);
    --w-amber-hot:var(--amber-hot,#E0A867);
    --w-green:    var(--cta-green,#1FA35B);
    --w-green-d:  var(--cta-green-dark,#17824A);
    --w-ease:     var(--ease,cubic-bezier(.22,1,.36,1));
    --w-ff:'Chivo',system-ui,-apple-system,sans-serif;
    --w-mono:'Chivo Mono',monospace;
    --w-inset:clamp(16px,3vw,30px);
    /* TRAP B — the placeholder must be dim against the VALUE colour, not just
       against the well. Measured: 3.79:1 from --bone and 3.15:1 from the well,
       so an empty field never reads as a filled one. */
    --w-hint:#7C7668;
    --w-well:#37281A;
    --w-well-hi:#43301F;
    --w-well-fo:#4A3623;
    --w-err:#C4713F;
    --w-err-lit:#E0705E;

    position:fixed;inset:0;z-index:9600;pointer-events:none;
    font-family:var(--w-ff);line-height:1.6;color:var(--w-ink);
  }
  *{box-sizing:border-box;margin:0}
  button{font-family:inherit;cursor:pointer;border:none;background:none;color:inherit}
  :host *:focus-visible{outline:2px solid var(--w-amber-lit);outline-offset:3px;border-radius:2px}
  [hidden]{display:none!important}
  @keyframes w-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
  @keyframes w-halo{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.14);opacity:0}}

  /* ── buttons: the theme's own .btn / .btn.ghost, including the green
        !important overrides from style blocks 2 and 4 ── */
  .cta{
    position:relative;display:inline-flex;align-items:center;justify-content:space-between;gap:12px;
    font-family:var(--w-mono);font-size:12px;letter-spacing:.2em;text-transform:uppercase;
    text-decoration:none;border-radius:0;padding:15px 20px;width:100%;
    transition:transform .3s var(--w-ease),background .3s,box-shadow .3s,color .3s,border-color .3s;
  }
  .cta.primary{background:var(--w-green);color:#fff;font-weight:700}
  .cta.primary:hover{background:var(--w-green-d);transform:translateY(-2px);box-shadow:0 12px 34px rgba(31,163,91,.28)}
  .cta.ghost{background:transparent;color:var(--w-green);border:1px solid var(--w-green)}
  .cta.ghost:hover{background:var(--w-green);color:#fff}
  .cta .lab{display:flex;flex-direction:column;line-height:1.25;text-align:left}
  .cta .sub{font-size:9.5px;letter-spacing:.26em;opacity:.82;font-weight:400}
  .cta .arr{transition:transform .3s var(--w-ease)}
  .cta:hover .arr{transform:translateX(5px)}

  /* ── launcher: green disc, white "?" that flips to "×" ── */
  .fab{
    position:fixed;right:var(--w-inset);bottom:var(--w-inset);pointer-events:auto;
    width:58px;height:58px;border-radius:50%;
    border:1px solid var(--w-green);background:var(--w-green);
    color:#fff;font-family:var(--w-ff);font-size:27px;font-weight:700;line-height:1;
    display:grid;place-items:center;
    box-shadow:0 10px 34px -8px rgba(31,163,91,.85);
    transition:transform .2s var(--w-ease),background .2s;
  }
  .fab:hover{background:var(--w-green-d);transform:scale(1.06)}
  .fab::before{
    content:"";position:absolute;inset:-7px;border-radius:50%;
    border:1px solid var(--w-green);opacity:.45;
    animation:w-halo 3.4s ease-in-out infinite;pointer-events:none;
  }
  .fab.open::before{animation:none;opacity:0}

  /* ── panel ── */
  .panel{
    position:fixed;right:var(--w-inset);bottom:calc(var(--w-inset) + 70px);
    pointer-events:auto;width:min(330px,calc(100vw - 32px));
    display:flex;flex-direction:column;gap:8px;
    /* TRAP C — opaque colour, gradient in background-image, so a hero photo
       behind the panel can never read through the copy. */
    background-color:var(--w-panel);
    background-image:linear-gradient(180deg,rgba(224,168,103,.05),transparent 42%);
    border:1px solid var(--w-line);border-top:1px solid var(--w-amber);
    padding:20px;box-shadow:0 26px 60px -18px rgba(0,0,0,.9);
    animation:w-rise .26s var(--w-ease);
  }
  .p-title{font-size:1.1rem;font-weight:400;letter-spacing:-.01em}
  .p-sub{margin-bottom:8px;font-size:13.5px;color:var(--w-mute)}
  .p-note{
    margin-top:10px;font-family:var(--w-mono);font-size:10.5px;letter-spacing:.14em;
    text-transform:uppercase;color:var(--w-amber-lit);
  }

  /* ── lead form ── */
  .scrim{
    position:fixed;inset:0;pointer-events:auto;
    background:rgba(21,13,7,.84);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);
    display:none;align-items:center;justify-content:center;padding:16px;overflow-y:auto;
  }
  .scrim.on{display:flex}
  .modal{
    position:relative;width:min(830px,100%);max-height:min(92svh,900px);
    display:flex;flex-direction:column;
    background-color:var(--w-panel);
    border:1px solid var(--w-line-am);border-top:2px solid var(--w-green);
    animation:w-rise .25s var(--w-ease);
  }
  .m-head{
    flex:none;display:flex;justify-content:space-between;align-items:flex-start;gap:20px;
    padding:20px 26px 14px;border-bottom:1px solid var(--w-line);
  }
  .m-eye{
    font-family:var(--w-mono);font-size:11px;letter-spacing:.28em;text-transform:uppercase;
    color:var(--w-amber-lit);
  }
  .m-title{
    font-family:var(--w-ff);font-weight:400;
    font-size:clamp(22px,3vw,28px);line-height:1.15;letter-spacing:-.02em;margin:6px 0 5px;
  }
  .m-sub{font-size:14px;color:var(--w-mute);max-width:46ch}
  .m-x{
    flex:none;width:34px;height:34px;border:1px solid var(--w-line);
    color:var(--w-mute);font-size:16px;line-height:1;transition:color .2s,border-color .2s;
  }
  .m-x:hover{color:var(--w-amber-hot);border-color:var(--w-amber)}
  /* head : body : foot, with only the MIDDLE one scrolling. The Send button
     lives in the foot, outside the scroll box, so it is on screen at every
     viewport height — no amount of trimming field padding can guarantee that
     on its own, and a submit button you have to hunt for is a dead form. */
  .m-body{flex:1 1 auto;min-height:0;display:flex;flex-direction:column;overflow:hidden;padding:0}
  .m-body form{display:flex;flex-direction:column;min-height:0;flex:1 1 auto}
  .m-scroll{
    flex:1 1 auto;min-height:0;overflow-y:auto;padding:18px 26px 8px;
    -webkit-overflow-scrolling:touch;   /* momentum scrolling on older iOS */
    overscroll-behavior:contain;        /* do not chain the scroll to the page behind */
    touch-action:pan-y;                 /* claim the vertical drag for this box */
  }

  /* The scrollbar CHROME is removed, not the scrolling. The UA paints a light
     default bar inside the shadow root — the theme's own ::-webkit-scrollbar
     rules are document-scoped and never reach in here — and a grey bar down a
     dark dialog looks like a bug. Hiding it keeps wheel, trackpad, touch and
     keyboard scrolling intact, which matters: max-height is what keeps the
     dialog on screen, and with scrolling actually disabled the Send button
     would be unreachable on any short viewport. */
  /* The BACKDROP never wants a bar. The scroll box does — but a slim amber
     one, not the UA default grey that looked broken against the dialog.
     overflow-y:auto only paints a bar when the content actually overflows,
     so this is self-hiding: absent when the form fits, present the moment a
     conditional field pushes it past the available height. Hiding it
     outright is what made a cut-off textarea read as a bug rather than as
     something to scroll to. */
  .scrim{scrollbar-width:none;-ms-overflow-style:none}
  .scrim::-webkit-scrollbar{width:0;height:0;background:transparent}
  .m-scroll,.done,.field textarea{scrollbar-width:thin;scrollbar-color:var(--w-amber) transparent}
  .m-scroll::-webkit-scrollbar,
  .done::-webkit-scrollbar,
  .field textarea::-webkit-scrollbar{width:5px;height:5px;background:transparent}
  .m-scroll::-webkit-scrollbar-thumb,
  .done::-webkit-scrollbar-thumb,
  .field textarea::-webkit-scrollbar-thumb{background:var(--w-amber)}

  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:13px}
  .span2{grid-column:1 / -1}
  .field{display:flex;flex-direction:column;gap:5px;min-width:0;position:relative}
  .field>label{
    font-family:var(--w-mono);font-size:10.5px;letter-spacing:.2em;line-height:1.45;
    text-transform:uppercase;color:var(--w-amber-lit);
  }
  .field .opt{color:var(--w-mute-2)}

  /* The well is LIGHTER than the card. A well darker than its container reads
     as a hole cut in the table rather than something you write on. */
  .field input,.field select,.field textarea{
    font-family:var(--w-ff);
    font-size:16px;                    /* 16px stops iOS zooming on focus */
    color:var(--w-ink);background:var(--w-well);
    border:0;border-radius:0;padding:11px 13px;width:100%;
    box-shadow:inset 0 0 0 1px var(--w-amber),inset 0 1px 0 rgba(224,168,103,.35);
    transition:background .16s ease,box-shadow .16s ease;
  }
  .field textarea{resize:vertical;min-height:66px;font-size:15px;line-height:1.55}
  .field input:hover,.field select:hover,.field textarea:hover{background:var(--w-well-hi)}
  .field input:focus,.field select:focus,.field textarea:focus{
    outline:none;background:var(--w-well-fo);
    box-shadow:inset 0 0 0 2px var(--w-green),0 0 0 4px rgba(31,163,91,.16);
  }
  /* TRAP B, all three empty states dimmed the same way */
  .field input::placeholder,.field textarea::placeholder{color:var(--w-hint);opacity:1}
  .field input:-webkit-autofill,
  .field input:-webkit-autofill:hover,
  .field input:-webkit-autofill:focus{
    -webkit-text-fill-color:var(--w-ink);
    caret-color:var(--w-ink);
    transition:background-color 8000s ease-in-out 0s;
    -webkit-box-shadow:inset 0 0 0 1px var(--w-amber),
                       inset 0 1px 0 rgba(224,168,103,.35),
                       inset 0 0 0 100px var(--w-well);
  }
  .field input:-webkit-autofill:focus{
    -webkit-box-shadow:inset 0 0 0 2px var(--w-green),
                       inset 0 0 0 100px var(--w-well-fo);
  }
  .field:not(.is-filled) select{color:var(--w-hint)}
  .field:not(.is-filled) input[type="date"]{color:var(--w-hint)}
  .field input[type="number"]{-moz-appearance:textfield;appearance:textfield}
  .field input[type="number"]::-webkit-outer-spin-button,
  .field input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
  .field select{appearance:none;cursor:pointer;padding-right:34px}
  .sel{position:relative}
  .sel::after{
    content:"";position:absolute;right:14px;top:19px;pointer-events:none;
    border:4px solid transparent;border-top-color:var(--w-amber-lit);
  }
  /* A jade rail marks a field that holds an answer. "Filled" means it has
     characters, NOT that they are valid, so it stands down for the error
     state — otherwise a malformed email wears the success ring while wrong. */
  .field.is-filled:not(.is-invalid) input,
  .field.is-filled:not(.is-invalid) select,
  .field.is-filled:not(.is-invalid) textarea{
    box-shadow:inset 4px 0 0 var(--w-green),inset 0 0 0 1px var(--w-amber);
  }
  .field.is-invalid input,.field.is-invalid select,.field.is-invalid textarea{
    box-shadow:inset 4px 0 0 var(--w-err),inset 0 0 0 1px var(--w-err);
  }

  /* AUTOFILL, re-declared per state.
     Chrome will not honour background-color on an autofilled input — only a
     covering inset box-shadow, and it has to be the LAST layer in the list.
     The base autofill rule further up is not enough on its own: the two rules
     above are BOTH more specific (.field.is-filled… beats .field input…) and
     later in the file, and their shadows carry no covering layer. An autofilled
     field is by definition filled, so those rules always applied and Chrome's
     pale blue came straight back. Every state that sets a shadow needs its own
     autofill twin. */
  .field.is-filled:not(.is-invalid) input:-webkit-autofill{
    -webkit-text-fill-color:var(--w-ink);
    -webkit-box-shadow:inset 4px 0 0 var(--w-green),
                       inset 0 0 0 1px var(--w-amber),
                       inset 0 0 0 100px var(--w-well);
  }
  .field.is-filled:not(.is-invalid) input:-webkit-autofill:focus{
    -webkit-box-shadow:inset 4px 0 0 var(--w-green),
                       inset 0 0 0 2px var(--w-green),
                       inset 0 0 0 100px var(--w-well-fo);
  }
  .field.is-invalid input:-webkit-autofill,
  .field.is-invalid input:-webkit-autofill:focus{
    -webkit-text-fill-color:var(--w-ink);
    -webkit-box-shadow:inset 4px 0 0 var(--w-err),
                       inset 0 0 0 1px var(--w-err),
                       inset 0 0 0 100px var(--w-well);
  }
  /* Back to display-toggled, which keeps the resting form tight. This was
     only ever reserved because an error growing the form pushed the Send
     button off screen — now that the footer is pinned outside the scroll
     box, the body just scrolls and the button stays put. */
  .err{
    display:none;
    font-family:var(--w-mono);font-size:10.5px;letter-spacing:.1em;line-height:1.35;
    text-transform:uppercase;color:var(--w-err-lit);
  }
  .field.is-invalid .err{display:block}

  .m-foot{
    position:relative;flex:none;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;
    margin:0;padding:14px 26px 22px;border-top:1px solid var(--w-line);
    background:var(--w-panel);
  }
  .m-foot::before{
    content:"";position:absolute;left:0;right:0;bottom:100%;height:24px;
    pointer-events:none;background:linear-gradient(transparent,var(--w-panel));
  }
  .m-foot p{margin:0;font-size:12.5px;color:var(--w-mute);max-width:34ch}
  .m-foot .cta{width:auto;justify-content:center;padding:13px 24px}
  .cta[disabled]{opacity:.6;cursor:progress}

  /* ── confirmation ── */
  .done{flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:16px;padding:18px 26px 26px}
  .done-mark{
    width:46px;height:46px;border-radius:50%;background:var(--w-green);color:#fff;
    display:grid;place-items:center;font-size:22px;font-weight:700;
  }
  .done-h{font-family:var(--w-ff);font-weight:300;text-transform:uppercase;font-size:1.6rem;letter-spacing:.04em;line-height:1.1;margin-bottom:10px}
  .done-lede{color:var(--w-mute);font-size:15px;max-width:48ch}
  .done-lede b{color:var(--w-ink);font-weight:600}
  .summary{display:grid;gap:1px;background:var(--w-line);border:1px solid var(--w-line)}
  .summary>div{background:var(--w-ground);padding:11px 15px;display:flex;justify-content:space-between;gap:18px;font-size:13.5px}
  .summary dt{font-family:var(--w-mono);font-size:10.5px;letter-spacing:.18em;text-transform:uppercase;color:var(--w-amber-lit)}
  .summary dd{text-align:right;color:var(--w-ink)}
  .done-next{border-top:1px solid var(--w-line);padding-top:18px;display:flex;flex-direction:column;gap:12px}
  .done-actions{display:flex;gap:10px;flex-wrap:wrap}
  .done-actions .cta{width:auto;justify-content:center;padding:13px 20px}
  .done-fine{font-size:12.5px;color:var(--w-mute-2);max-width:52ch}
  /* A real button, but AMBER — not a third green one. The two green buttons
     beside it are the things we want pressed; this is the utility escape
     hatch. Same size and shape so the row reads as one set, different colour
     so the hierarchy is obvious without reading the labels. */
  .done-again{
    display:inline-flex;align-items:center;justify-content:center;
    padding:13px 20px;
    font-family:var(--w-mono);font-size:12px;letter-spacing:.2em;text-transform:uppercase;
    color:var(--w-amber-lit);background:transparent;
    border:1px solid var(--w-line-am);
    transition:color .3s,border-color .3s,background .3s;
  }
  .done-again:hover{
    color:var(--w-amber-hot);border-color:var(--w-amber);
    background:rgba(224,168,103,.07);
  }

  /* SHORT VIEWPORTS.
     The constraint on this dialog is viewport HEIGHT, not width, so that is
     what it responds to. A single spacing scale cannot serve both: roomy
     enough to match the mock on a tall screen is ~640px of content, which
     overflows a 755px-tall laptop once the dialog is capped at 92svh and an
     error line appears. Rather than compromise everywhere, the tall-screen
     rhythm stays generous and short screens compact automatically.
     Nothing is removed and no type drops below 16px in the inputs, so iOS
     still will not zoom on focus. */
  @media(max-height:820px){
    .m-head{padding:14px 26px 10px}
    .m-title{font-size:24px;margin:4px 0 3px}
    .m-sub{font-size:13px}
    .m-scroll{padding:12px 26px 6px}
    .grid2{gap:9px}
    .field{gap:4px}
    .field input,.field select,.field textarea{padding:9px 12px}
    .field textarea{min-height:52px}
    .sel::after{top:19px}
    .m-foot{padding:10px 26px 14px}
    .m-foot .cta{padding:11px 22px}
  }
  /* MOBILE.
     Single column means nine stacked fields — taller than any phone, always.
     So rather than fight it: the dialog becomes a full-screen sheet (no scrim
     gutter, no height cap) to use every available pixel, and the scroll box
     gets its scrollbar BACK.

     The hidden-chrome rule above is right on desktop, where the panel usually
     fits and a grey bar just looks broken. Here it is wrong: the form must
     scroll, and with no bar there is nothing telling the user that. A slim
     amber thumb matches the theme and restores the affordance.

     The header and footer stay pinned, so Send is on screen throughout. */
  @media(max-width:620px){
    .scrim{padding:0;align-items:stretch;justify-content:stretch}
    .modal{width:100%;max-height:100svh;min-height:100svh;border-left:0;border-right:0;border-bottom:0}
    .grid2{grid-template-columns:1fr}
    .m-head{padding:16px 20px 12px}
    .m-scroll{padding:16px 20px 8px}
    .m-foot{padding:12px 20px 16px}
    .done{padding:18px 20px 22px}
    .m-foot .cta{width:100%}
  }
  @media(prefers-reduced-motion:reduce){
    .fab,.cta,.field input,.field select,.field textarea{transition:none}
    .fab::before,.panel,.modal{animation:none}
    .fab:hover,.cta:hover{transform:none}
    .cta:hover .arr{transform:none}
  }
  `;

  var HTML = `
  <div class="panel" id="panel" role="dialog" aria-label="Three ways to reach ALIA" hidden>
    <p class="p-title">Three ways in</p>
    <p class="p-sub">Which one are you here for?</p>
    <button type="button" class="cta primary" data-act="corp">
      <span class="lab"><span class="sub">Priority</span>Corporate · Private events</span><span class="arr">&rarr;</span>
    </button>
    <a class="cta ghost" href="${CFG.openTable}" target="_blank" rel="noopener">
      <span class="lab">Restaurant · Book a table</span><span class="arr">&rarr;</span>
    </a>
    <a class="cta ghost" href="${CFG.phoneHref}">
      <span class="lab">Club · Call the door</span><span class="arr">&rarr;</span>
    </a>
    <p class="p-note">${CFG.phoneLabel}</p>
  </div>

  <button class="fab" id="fab" type="button" aria-label="How to reach us" aria-expanded="false" aria-controls="panel">?</button>

  <div class="scrim" id="scrim">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="m-title">
      <div class="m-head">
        <div>
          <p class="m-eye">Private events</p>
          <h2 class="m-title" id="m-title">Request a date</h2>
          <p class="m-sub" id="m-sub">Tell us the date and the headcount and we will come back to you.</p>
        </div>
        <button class="m-x" id="m-x" type="button" aria-label="Close">&#10005;</button>
      </div>

      <div class="m-body">
        <form id="form" novalidate>
          <div class="m-scroll">
          <div class="grid2">
            <div class="field" data-empty="Your name, please" data-bad="Too short">
              <label for="f-name">Your name</label>
              <input id="f-name" name="Name" type="text" autocomplete="name" placeholder="Full name" maxlength="80" required>
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Company, please" data-bad="Too short">
              <label for="f-company">Company</label>
              <input id="f-company" name="Company" type="text" autocomplete="organization" placeholder="Company" maxlength="80" required>
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Email, please" data-bad="Enter a valid email">
              <label for="f-email">Work email</label>
              <!-- NO pattern attribute here. This markup lives in a JS
                   template literal, where a backslash is an escape: a
                   backslash-s collapses to a plain s. The browser received a
                   class meaning "anything except @ and the letter s", so every
                   address with an s in the domain was rejected. type="email"
                   plus customCheck() validate properly, and customCheck lives
                   in real JS where a regex literal is safe. -->
              <input id="f-email" name="Email" type="email" autocomplete="email" placeholder="you@company.com"
                     maxlength="120" required>
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Phone, please" data-bad="Too short to dial">
              <label for="f-phone">Phone</label>
              <input id="f-phone" name="Phone" type="tel" autocomplete="tel" placeholder="514 000 0000"
                     inputmode="tel" maxlength="24" required>
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Pick one">
              <label for="f-event">Event</label>
              <div class="sel">
                <select id="f-event" name="Event type" required>
                  <option value="" selected>Choose one</option>
                  <option>Year-end party</option>
                  <option>Client dinner</option>
                  <option>Full buyout</option>
                  <option>Cocktail reception</option>
                  <option>Product launch</option>
                  <option>Something else</option>
                </select>
              </div>
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Roughly how many?" data-bad="Numbers only">
              <label for="f-guests">Guests</label>
              <!-- text + inputmode, not type=number: number accepts "e", "+"
                   and "-", and its spinner is awkward on mobile -->
              <input id="f-guests" name="Guests" type="text" inputmode="numeric" maxlength="4" placeholder="40" required>
              <span class="err"></span>
            </div>
            <div class="field span2" id="other-wrap" data-empty="Tell us briefly" hidden>
              <label for="f-other">Tell us about the event</label>
              <input id="f-other" name="Event detail" type="text" placeholder="What are you planning?" maxlength="120">
              <span class="err"></span>
            </div>
            <div class="field" data-empty="Pick a date">
              <label for="f-date">Preferred date</label>
              <input id="f-date" name="Preferred date" type="date" required>
              <span class="err"></span>
            </div>
            <div class="field">
              <label for="f-budget">Budget per head <span class="opt">(optional)</span></label>
              <div class="sel">
                <select id="f-budget" name="Budget per head">
                  <option value="" selected>Not sure yet</option>
                  <option>$95 to $150</option>
                  <option>$150 to $225</option>
                  <option>$225 and up</option>
                </select>
              </div>
            </div>
            <div class="field span2">
              <label for="f-notes">Anything we should know <span class="opt">(optional)</span></label>
              <textarea id="f-notes" name="Notes" placeholder="Dietary needs, AV, a speech, a hard end time." maxlength="600"></textarea>
            </div>
          </div>
          </div>
          <div class="m-foot">
            <p id="m-fine"></p>
            <button class="cta primary" type="submit" id="submit"><span class="lab">Send request</span></button>
          </div>
        </form>

        <div class="done" id="done" hidden aria-live="polite">
          <div class="done-mark" aria-hidden="true">&#10003;</div>
          <div>
            <h3 class="done-h" id="done-h">Thank you.</h3>
            <p class="done-lede" id="done-lede"></p>
          </div>
          <dl class="summary" id="summary"></dl>
          <div class="done-next">
            <p class="m-eye">If you need us sooner</p>
            <div class="done-actions">
              <a class="cta ghost" href="${CFG.phoneHref}"><span class="lab">Call ${CFG.phoneLabel}</span></a>
              <button type="button" class="done-again" id="done-again">Send another request</button>
            </div>
            <p class="done-fine" id="done-fine"></p>
          </div>
        </div>
      </div>
    </div>
  </div>
  `;

  /* ─────────────────────────── mount ─────────────────────────── */
  var host = document.createElement('div');
  host.id = 'alia-wgt-host';
  /* Lenis sees the retargeted host, not our inner nodes, so the opt-out
     attribute belongs here or touch scrolling inside the dialog dies. */
  host.setAttribute('data-lenis-prevent', '');
  document.body.appendChild(host);
  var root = host.attachShadow({ mode: 'open' });
  root.innerHTML = '<style>' + CSS + '</style>' + HTML;

  /* the theme's own sticky dock shares this corner and is a strict subset of
     the widget's actions, so it stands down */
  var hide = document.createElement('style');
  hide.id = 'alia-wgt-dock-hide';
  hide.textContent = '.alia-cta-dock{display:none!important}';
  document.head.appendChild(hide);

  var $ = function (s) { return root.getElementById(s); };
  var fab = $('fab'), panel = $('panel'), scrim = $('scrim'), form = $('form');
  var done = $('done'), summary = $('summary'), submit = $('submit');
  var otherWrap = $('other-wrap'), otherInput = $('f-other'), eventSel = $('f-event');

  /* A service note, as in the mock. The prefilled-email mechanics are
     explained on the confirmation screen instead of pre-emptively here. */
  $('m-fine').textContent = 'We will confirm availability and a starting menu.';

  /* ── session guard ──
     Remembers a sent enquiry so reopening the form does not present a primed
     blank form, which is the obvious way to end up with the same lead twice.
     sessionStorage, not local: a new tab or a later visit starts clean. This
     guards against an accidental double-send, it is not a lockout.
     Safari in private mode throws on write, so every access is wrapped —
     a failure just means no guard, never a broken form. */
  var SENT_KEY = 'alia_sent_' + (CFG.site || 'site');
  function readSent() {
    try { var raw = sessionStorage.getItem(SENT_KEY); return raw ? JSON.parse(raw) : null; }
    catch (e) { return null; }
  }
  function rememberSent(p) {
    try { sessionStorage.setItem(SENT_KEY, JSON.stringify(p.data)); } catch (e) {}
  }
  function forgetSent() { try { sessionStorage.removeItem(SENT_KEY); } catch (e) {} }

  /* ── panel ── */
  function setPanel(open) {
    panel.hidden = !open;
    fab.textContent = open ? '✕' : '?';
    fab.classList.toggle('open', open);
    fab.setAttribute('aria-expanded', String(open));
    fab.setAttribute('aria-label', open ? 'Close' : 'How to reach us');
  }
  fab.addEventListener('click', function (e) { e.stopPropagation(); setPanel(panel.hidden); });
  document.addEventListener('click', function (e) {
    if (panel.hidden) return;
    var p = e.composedPath();
    if (p.indexOf(panel) === -1 && p.indexOf(fab) === -1) setPanel(false);
  });

  /* ── modal ── */
  function lenis() { return window.ALIA_LENIS || null; }
  var lastFocus = null;

  function openModal() {
    lastFocus = document.activeElement;
    setPanel(false);

    /* already sent this session? show what was sent, not a primed form —
       otherwise the obvious move is to fill it in again and post a duplicate */
    var prev = readSent();
    if (prev) {
      confirmView({ data: prev }, true, true);
      scrim.classList.add('on');
      var l0 = lenis(); if (l0 && l0.stop) l0.stop();
      return;
    }

    form.hidden = false; done.hidden = true;
    $('m-title').textContent = 'Request a date';
    $('m-sub').textContent = 'Tell us the date and the headcount and we will come back to you.';
    scrim.classList.add('on');
    var l = lenis(); if (l && l.stop) l.stop();
    setTimeout(function () { $('f-name').focus(); }, 30);
  }
  function closeModal() {
    scrim.classList.remove('on');
    var l = lenis(); if (l && l.start) l.start();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  $('m-x').addEventListener('click', closeModal);
  scrim.addEventListener('click', function (e) { if (e.target === scrim) closeModal(); });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (scrim.classList.contains('on')) closeModal();
    else if (!panel.hidden) setPanel(false);
  });

  /* TRAP A — ONE handler, on document, walking composedPath(). This is what
     makes every Corporate button on the page work, not just the two in here. */
  document.addEventListener('click', function (e) {
    var path = e.composedPath ? e.composedPath() : [e.target];
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.getAttribute && el.getAttribute('data-act') === 'corp') {
        e.preventDefault();
        openModal();
        return;
      }
    }
  });

  /* ── conditional "Something else" ──
     TRAP H — a hidden control that is still `required` fails checkValidity()
     on something the browser cannot focus or scroll to, so the form refuses
     to submit with nothing on screen explaining why. Toggle required WITH
     visibility, never independently. */
  function syncOther() {
    var other = eventSel.value === 'Something else';
    otherWrap.hidden = !other;
    if (other) {
      otherInput.setAttribute('required', '');
      /* it appears mid-form and pushes everything below it down, so bring it
         into view instead of leaving the user to wonder what moved */
      otherWrap.scrollIntoView({ block: 'nearest' });
    }
    else { otherInput.removeAttribute('required'); otherInput.value = ''; otherWrap.classList.remove('is-invalid', 'is-filled'); }
  }
  eventSel.addEventListener('change', syncOther);
  syncOther();

  /* ── field state ──
     State lives on the .field WRAPPER, so every rule sits at one specificity
     tier and the CSS order IS the priority order. It also sidesteps
     :placeholder-shown, which never reports a date input or an unchosen
     <select> as empty. */
  var fields = [].slice.call(form.querySelectorAll('.field'));
  function ctrl(f) { return f.querySelector('input, select, textarea'); }
  /* Beyond the browser's own validity: what counts as a usable answer.
     Returns true, or the message explaining what is wrong. Emptiness is left
     to `required` so a blank optional field never trips this. */
  function customCheck(c, v) {
    if (v === '') return true;
    if (c.name === 'Guests') {
      if (!/^\d+$/.test(v)) return 'Numbers only';
      if (parseInt(v, 10) < 1) return 'At least 1';
      return true;   /* deliberately no upper bound — a big number is a big lead */
    }
    if (c.name === 'Phone')  return v.replace(/\D/g, '').length >= 8 ? true : 'Too short to dial';
    if (c.name === 'Email')  return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v) ? true : 'Enter a valid email';
    if (c.name === 'Name' || c.name === 'Company') return v.length >= 2 ? true : 'Too short';
    return true;
  }

  function syncField(f) {
    var c = ctrl(f); if (!c) return;
    var v = String(c.value || '').trim();
    f.classList.toggle('is-filled', v !== '');
    if (f.dataset.touched !== '1') return;

    var custom = customCheck(c, v);
    var bad = !c.checkValidity() || custom !== true;
    f.classList.toggle('is-invalid', bad);

    /* say what is actually wrong, rather than "Required" for every failure */
    var err = f.querySelector('.err');
    if (err) {
      err.textContent = !bad ? ''
        : v === '' ? (f.dataset.empty || 'Required')
        : typeof custom === 'string' ? custom
        : (f.dataset.bad || 'Check this');
    }
  }
  function syncAll() { fields.forEach(syncField); }
  fields.forEach(function (f) {
    var c = ctrl(f); if (!c) return;
    c.addEventListener('input', function () { syncField(f); });
    c.addEventListener('change', function () { syncField(f); });
    c.addEventListener('blur', function () { f.dataset.touched = '1'; syncField(f); });
  });
  syncAll();

  $('f-date').min = new Date().toISOString().slice(0, 10);

  function niceDate(iso) {
    try {
      var p = iso.split('-');
      return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString('en-CA',
        { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch (err) { return iso; }
  }

  function collect() {
    var data = {}, lines = [];
    summary.innerHTML = '';
    [].forEach.call(form.elements, function (el) {
      if (!el.name || !el.value) return;
      var shown = el.name === 'Preferred date' ? niceDate(el.value) : el.value;
      data[el.name] = String(el.value).trim();
      var row = document.createElement('div');
      var dt = document.createElement('dt'); dt.textContent = el.name;
      var dd = document.createElement('dd'); dd.textContent = shown;
      row.appendChild(dt); row.appendChild(dd);
      summary.appendChild(row);
      lines.push(el.name + ': ' + shown);
    });
    return { data: data, text: lines.join('\n') };
  }

  function confirmView(p, delivered, returning) {
    var first = (p.data.Name || '').trim().split(/\s+/)[0];
    $('m-title').textContent = returning ? 'Already sent' : 'Request received';
    $('m-sub').textContent = returning ? 'We have this one.'
      : delivered ? 'The events team has it.' : 'One more step.';
    $('done-h').textContent = returning ? (first ? 'Thanks, ' + first + '.' : 'Thanks.')
      : delivered ? (first ? 'Thank you, ' + first + '.' : 'Thank you.') : 'Give us a call.';
    $('done-lede').innerHTML = 'Your request for <b>' + (p.data.Guests || '') + ' guests</b> on <b>' +
      niceDate(p.data['Preferred date'] || '') + '</b> is with the events team, and we will come back to you about ' +
      'availability and a starting menu.';
    $('done-fine').textContent = returning
      ? 'Sent from this device. Use the button below if you need to send a different date or headcount.'
      : delivered
        ? 'A copy of this request is with the events team.'
        : 'We could not submit this automatically. Please call ' + CFG.phoneLabel + ' with your date and guest count and the team will pick it up.';
    /* only offer a fresh form once something has actually been sent */
    $('done-again').hidden = !(delivered || returning);
    form.hidden = true; done.hidden = false;
    done.scrollIntoView({ block: 'nearest' });
  }

  /* the deliberate way back to a blank form, for a genuinely different enquiry */
  $('done-again').addEventListener('click', function () {
    forgetSent();
    form.reset();
    fields.forEach(function (f) {
      delete f.dataset.touched;
      f.classList.remove('is-filled', 'is-invalid');
      var e = f.querySelector('.err'); if (e) e.textContent = '';
    });
    syncOther();
    syncAll();
    done.hidden = true; form.hidden = false;
    $('m-title').textContent = 'Request a date';
    $('m-sub').textContent = 'Tell us the date and the headcount and we will come back to you.';
    $('f-name').focus();
  });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    fields.forEach(function (f) { f.dataset.touched = '1'; });
    syncAll();
    /* syncAll has just written is-invalid using BOTH native validity and
       customCheck, so this catches the custom failures too */
    var bad = form.querySelector('.field.is-invalid input, .field.is-invalid select, .field.is-invalid textarea');
    if (bad) { bad.focus(); return; }

    var p = collect();
    p.data.Site = CFG.site;
    p.data.Page = location.href;

    /* No endpoint configured means the form genuinely cannot deliver. Say
       so and give the phone number — promising a reply nobody will receive
       is worse than admitting the form is not wired up. */
    if (DEMO) { confirmView(p, false); return; }

    submit.disabled = true;
    submit.querySelector('.lab').textContent = 'Sending';
    /* text/plain avoids a CORS preflight, which Apps Script endpoints reject */
    fetch(CFG.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(p.data)
    }).then(function () {
      rememberSent(p);
      confirmView(p, true);      /* posted — say so */
    }).catch(function () {
      confirmView(p, false);     /* genuinely failed — send them to the phone */
    }).then(function () {
      submit.disabled = false;
      submit.querySelector('.lab').textContent = 'Send request';
    });
  });
})();
